import { artistHref } from '@/features/artists/artistHref'
import { shareSong } from '@/features/sharing/ShareSheet'
import {
  Info,
  RotateCcw,
  Sparkles,
  Trash2,
  TriangleAlert,
} from 'lucide-react'

import type { LibraryItem } from '@/api/types'
import { songActions } from '@/components/songActions'
import { TrackRow, type TrackRowMenuItem } from '@/components/TrackRow'
import { Button } from '@/components/ui'

import { SelectableTrackRow } from './SelectableTrackRow'
import { useLongPress } from './useLongPress'

export interface LibraryTrackRowProps {
  item: LibraryItem
  isActive: boolean
  isPlaying: boolean
  isFavorite: boolean
  /** 0-100, from the live job stream. Undefined when no job is running. */
  progress?: number
  isRetrying: boolean
  onPlay: () => void
  onToggleFavorite: () => void
  onPlayNext: () => void
  onAddToQueue: () => void
  onAddToPlaylist: () => void
  /** Open the neighbours of this track's artist. Omitted when the artist cannot
   *  be looked up on a provider, which is the only case where it would 404. */
  onMoreLikeThis?: () => void
  onDetails?: () => void
  onRemove: () => void
  onRetry: () => void
  /** The list is being multi-selected: the row becomes one big checkbox. */
  selectionActive?: boolean
  isSelected?: boolean
  onToggleSelected?: (shiftKey: boolean) => void
  /** Hold on touch to start selecting. Omit once selection is already on. */
  onLongPress?: () => void
}

function brokenExplanation(item: LibraryItem): string {
  if (item.status === 'missing') {
    return 'The audio file is no longer on the server. Download it again to play it.'
  }
  return item.error_message || 'The download did not finish. It can usually be restarted.'
}

export function LibraryTrackRow({
  item,
  isActive,
  isPlaying,
  isFavorite,
  progress,
  isRetrying,
  onPlay,
  onToggleFavorite,
  onPlayNext,
  onAddToQueue,
  onAddToPlaylist,
  onMoreLikeThis,
  onDetails,
  onRemove,
  onRetry,
  selectionActive = false,
  isSelected = false,
  onToggleSelected,
  onLongPress,
}: LibraryTrackRowProps) {
  const longPress = useLongPress(onLongPress)

  const broken = item.status === 'failed' || item.status === 'missing'
  const playable = item.status === 'ready' && item.stream_url !== null

  const menuItems: TrackRowMenuItem[] = [
    ...songActions({ share: () => shareSong(item.id), playNext: onPlayNext, addToQueue: onAddToQueue, addToPlaylist: onAddToPlaylist, playbackDisabled: !playable }),
    ...(onDetails ? [{ label: 'Song details', icon: Info, context: true, onSelect: onDetails }] : []),
    // The way out of the library and into music you do not have yet. Deliberately
    // in the same menu as "add to playlist": both are things you do to a song you
    // just heard and liked.
    ...(onMoreLikeThis
      ? [{ label: 'More like this', context: true, icon: Sparkles, onSelect: onMoreLikeThis }]
      : []),
    {
      label: 'Remove from library',
      icon: Trash2,
      onSelect: onRemove,
      destructive: true,
    },
  ]

  if (selectionActive && onToggleSelected) {
    return <SelectableTrackRow item={item} selected={isSelected} onToggle={onToggleSelected} />
  }

  const row = (
    <TrackRow
      title={item.track.title}
      artistName={item.track.artist_name}
      artistHref={artistHref(item.track)}
      albumName={item.track.album_name}
      durationMs={item.track.duration_ms}
      coverUrl={item.cover_url}
      isActive={isActive}
      isPlaying={isPlaying}
      status={item.status}
      progress={progress}
      isFavorite={isFavorite}
      onPlay={onPlay}
      onToggleFavorite={onToggleFavorite}
      menuItems={menuItems}
    />
  )

  if (!broken) return <div {...longPress}>{row}</div>

  return (
    <div className="my-1 overflow-hidden rounded-lg border border-border bg-surface-sunken">
      {/* The hold only covers the row itself — the retry button below is a
          deliberate press, not a gesture. */}
      <div {...longPress}>{row}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-3 py-2">
        <TriangleAlert className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-text-muted">{brokenExplanation(item)}</p>
        <Button
          size="sm"
          variant="secondary"
          loading={isRetrying}
          onClick={onRetry}
          aria-label={`Download ${item.track.title} again`}
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          {item.status === 'missing' ? 'Download again' : 'Retry'}
        </Button>
      </div>
    </div>
  )
}
